import { useEffect, useRef, useState } from "react";
import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { FilterValues } from "../../../../data-model/filterValues";
import { TableNames } from "../../../../enums/tableNames";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { generateFilterPayloadForMedicalPaOrders } from "../utils/generateFilterPayloadForMedicalPaOrders";
import { useDefaultFacetFiltersForRoute } from "./useDefaultFacetFiltersForRoute";

/**
 * Generates and maintains the Postgres search payload from active filters, search term,
 * and the persisted sort state from `tablesContext` (`sortByField` / `sortAscending`).
 * Defers payload generation until after the initial data fetch completes, then recalculates
 * whenever any of: filters, search term, org ID, sort field, or sort direction change.
 * Uses a single effect with a ref guard so the payload is built immediately once the
 * initial fetch finishes.
 *
 * Re-running on sort changes is what makes column-header clicks trigger a server-side
 * refetch via the downstream filter/search effect, since `CustomTable` writes
 * `sortByField`/`sortAscending` into context on each sort click.
 *
 * Route-derived default facets (via {@link useDefaultFacetFiltersForRoute})
 * are merged into every payload so worklists like `/orders-to-process`
 * stay scoped to `master_auth_status: orders_to_process` for every fetch.
 *
 * @param validFilters - Currently active filter values for the medical PA orders table
 * @param searchTerm - The current free-text search query
 * @param initialDataFetchingExecuted - Whether the initial data load has completed
 * @returns The current filter payload and its setter
 */
export const useFilterPayload = (
  validFilters: FilterValues[],
  searchTerm: string,
  initialDataFetchingExecuted: boolean,
) => {
  const orgId = getOrgIdForFetchExternalWorklist();
  const { getTableDataFromContext } = useTablesContext();
  const tableAttributes = getTableDataFromContext(
    TableNames.MEDICAL_PA_ORDERS_NEW,
  );
  const sortByField = tableAttributes?.sortByField;
  const sortAscending = tableAttributes?.sortAscending;
  const defaultFacetFilters = useDefaultFacetFiltersForRoute();

  const [filterPayload, setFilterPayload] =
    useState<SearchForMedicalPaOrdersFromPostgresParams | null>(null);

  const hasInitialFetchCompleted = useRef(false);

  useEffect(() => {
    if (initialDataFetchingExecuted && !hasInitialFetchCompleted.current) {
      hasInitialFetchCompleted.current = true;
    }

    if (!hasInitialFetchCompleted.current) return;

    const payload = generateFilterPayloadForMedicalPaOrders(
      validFilters,
      searchTerm,
      orgId,
      sortByField,
      sortAscending,
      defaultFacetFilters,
    );
    setFilterPayload(payload);
  }, [
    initialDataFetchingExecuted,
    validFilters,
    searchTerm,
    orgId,
    sortByField,
    sortAscending,
    defaultFacetFilters,
  ]);

  return { filterPayload, setFilterPayload };
};
